import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, Video, Search, FileText, Download, Play, 
  ExternalLink, FolderOpen, Filter, X
} from 'lucide-react';
import { saveAs } from 'file-saver';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { trackFeatureUsage } from '../utils/analytics';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

// Helper to extract YouTube video ID
const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

// Helper to extract Vimeo video ID
const getVimeoId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:vimeo\.com\/)(\d+)/);
  return match ? match[1] : null;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

function Resources({ user }) {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    fetchData();
    // Track resources page visit
    trackFeatureUsage('Resources');
  }, []);

  const fetchData = async () => {
    try {
      const [resourcesRes, categoriesRes] = await Promise.all([
        fetch(`${API}/resources`, { credentials: 'include' }),
        fetch(`${API}/resource-categories`, { credentials: 'include' })
      ]);
      
      if (resourcesRes.ok) {
        setResources(await resourcesRes.json());
      }
      if (categoriesRes.ok) {
        setCategories(await categoriesRes.json());
      }
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (resource) => {
    try {
      // Fetch the file with credentials
      const response = await fetch(`${API}/resources/${resource.resource_id}/download`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to download resource');
      }
      
      // Get the blob and trigger download
      const blob = await response.blob();
      saveAs(blob, resource.file_name || 'download');
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download resource');
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = 
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || resource.category_id === selectedCategory;
    const matchesType = selectedType === 'all' || resource.resource_type === selectedType;
    return matchesSearch && matchesCategory && matchesType;
  });

  const documents = filteredResources.filter(r => r.resource_type === 'document');
  const videos = filteredResources.filter(r => r.resource_type === 'video');

  const renderVideoPlayer = (resource) => {
    const youtubeId = getYouTubeId(resource.url);
    const vimeoId = getVimeoId(resource.url);
    
    if (youtubeId) {
      return (
        <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title={resource.title}
            className="w-full h-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>
      );
    }
    
    if (vimeoId) {
      return (
        <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}`}
            title={resource.title}
            className="w-full h-full"
            allowFullScreen
            allow="autoplay; fullscreen; picture-in-picture"
          />
        </div>
      );
    }
    
    // For uploaded videos
    if (resource.file_path) {
      return (
        <div className="aspect-video w-full rounded-lg overflow-hidden bg-black flex items-center justify-center">
          <div className="text-center text-white">
            <Video className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm opacity-75">Video file</p>
            <Button 
              variant="outline" 
              className="mt-2 text-white border-white hover:bg-white/10"
              onClick={() => handleDownload(resource)}
            >
              <Download className="h-4 w-4 mr-2" />
              Download to Watch
            </Button>
          </div>
        </div>
      );
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFE]">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#80298F]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Client Resources</h1>
          <p className="text-gray-600">Helpful documents and videos to guide you through your case</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
            {/* Search */}
            <div className="flex-1 relative min-w-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 py-3 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Filter className="h-5 w-5 text-gray-400 hidden sm:block" />
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-[180px] h-[44px] sm:h-9 text-base sm:text-sm">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Type Filter */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-full sm:w-[150px] h-[44px] sm:h-9 text-base sm:text-sm">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="document">Documents</SelectItem>
                  <SelectItem value="video">Videos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Clear Filters */}
            {(searchQuery || selectedCategory !== 'all' || selectedType !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedType('all');
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center gap-4 mb-6 text-sm text-gray-600">
          <span>{filteredResources.length} resources</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <FileText className="h-4 w-4" />
            {documents.length} documents
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Video className="h-4 w-4" />
            {videos.length} videos
          </span>
        </div>

        {filteredResources.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">No resources found</p>
              {(searchQuery || selectedCategory !== 'all' || selectedType !== 'all') && (
                <p className="text-sm text-gray-400">Try adjusting your filters</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {/* Group by category if no category filter */}
            {selectedCategory === 'all' ? (
              <>
                {/* Categorized resources */}
                {categories.filter(cat => 
                  filteredResources.some(r => r.category_id === cat.category_id)
                ).map(category => (
                  <div key={category.category_id} className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                      <FolderOpen className="h-5 w-5 text-[#80298F]" />
                      {category.name}
                      {category.description && (
                        <span className="text-sm font-normal text-gray-500">- {category.description}</span>
                      )}
                    </h2>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {filteredResources
                        .filter(r => r.category_id === category.category_id)
                        .map(resource => (
                          <ResourceCard 
                            key={resource.resource_id} 
                            resource={resource}
                            onDownload={handleDownload}
                            playingVideo={playingVideo}
                            setPlayingVideo={setPlayingVideo}
                            renderVideoPlayer={renderVideoPlayer}
                          />
                        ))}
                    </div>
                  </div>
                ))}
                
                {/* Uncategorized resources */}
                {filteredResources.some(r => !r.category_id) && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                      <FolderOpen className="h-5 w-5 text-[#80298F]" />
                      General Resources
                    </h2>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {filteredResources
                        .filter(r => !r.category_id)
                        .map(resource => (
                          <ResourceCard 
                            key={resource.resource_id} 
                            resource={resource}
                            onDownload={handleDownload}
                            playingVideo={playingVideo}
                            setPlayingVideo={setPlayingVideo}
                            renderVideoPlayer={renderVideoPlayer}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredResources.map(resource => (
                  <ResourceCard 
                    key={resource.resource_id} 
                    resource={resource}
                    onDownload={handleDownload}
                    playingVideo={playingVideo}
                    setPlayingVideo={setPlayingVideo}
                    renderVideoPlayer={renderVideoPlayer}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Video Modal */}
      {playingVideo && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPlayingVideo(null)}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg">{playingVideo.title}</h3>
              <Button variant="ghost" size="sm" onClick={() => setPlayingVideo(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4">
              {renderVideoPlayer(playingVideo)}
            </div>
            <div className="p-4 border-t bg-gray-50">
              <p className="text-sm text-gray-600">{playingVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Resource Card Component
function ResourceCard({ resource, onDownload, playingVideo, setPlayingVideo, renderVideoPlayer }) {
  const isDocument = resource.resource_type === 'document';
  const isVideo = resource.resource_type === 'video';
  const youtubeId = getYouTubeId(resource.url);
  const vimeoId = getVimeoId(resource.url);
  
  return (
    <Card className="hover:shadow-md transition-shadow overflow-hidden">
      {/* Thumbnail/Preview for Videos */}
      {isVideo && (youtubeId || vimeoId) && (
        <div 
          className="aspect-video bg-gray-100 relative cursor-pointer group"
          onClick={() => setPlayingVideo(resource)}
        >
          {youtubeId && (
            <img 
              src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
              alt={resource.title}
              className="w-full h-full object-cover"
            />
          )}
          {vimeoId && (
            <div className="w-full h-full bg-gradient-to-br from-[#80298F] to-[#FAA519] flex items-center justify-center">
              <Video className="h-12 w-12 text-white opacity-75" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <div className="bg-white rounded-full p-3">
              <Play className="h-8 w-8 text-[#80298F]" />
            </div>
          </div>
        </div>
      )}
      
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 p-2 rounded-lg ${isDocument ? 'bg-purple-100' : 'bg-amber-100'}`}>
            {isDocument ? (
              <FileText className="h-6 w-6 text-[#80298F]" />
            ) : (
              <Video className="h-6 w-6 text-[#FAA519]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{resource.title}</h3>
            <p className="text-sm text-gray-600 line-clamp-2 mt-1">{resource.description}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
              <span className="px-2 py-0.5 bg-gray-100 rounded">{resource.category_name}</span>
              {resource.file_size && (
                <span>{formatFileSize(resource.file_size)}</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="mt-4 flex gap-2">
          {isDocument && resource.file_path && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => onDownload(resource)}
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
          )}
          {isDocument && resource.url && !resource.file_path && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => window.open(resource.url, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Open
            </Button>
          )}
          {isVideo && (youtubeId || vimeoId) && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => setPlayingVideo(resource)}
            >
              <Play className="h-4 w-4 mr-1" />
              Watch
            </Button>
          )}
          {isVideo && resource.file_path && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => onDownload(resource)}
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
          )}
          {isVideo && resource.url && !youtubeId && !vimeoId && !resource.file_path && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => window.open(resource.url, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Watch
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default Resources;
